import { useEffect, useMemo, useState, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { OrderCalcBreakdown } from "@/components/orders/OrderCalcBreakdown";
import { StudiesTooltip, lineSourceCns } from "@/components/orders/StudiesTooltip";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TherapeuticPicker } from "@/components/orders/TherapeuticPicker";
import { downloadXlsx } from "@/lib/download";
import { toast } from "sonner";
import { useSortState, sortRows, SortTh } from "@/lib/sortable";
import {
  PackageSearch, Truck, FileSpreadsheet, FileText as FileTextIcon,
  Loader2, ChevronDown, ChevronRight, AlertTriangle, Boxes, Info, Ban, X, Lightbulb, ArrowRightLeft, Pill,
  Pencil, Check, TrendingUp, TrendingDown, Minus, History,
} from "lucide-react";

/**
 * Iter-59: calcula la referencia del mes anterior normalizada a la ventana
 * del pedido actual (`days`). Prefiere el override manual del estudio previo
 * (dato más fiable: "lo que la farmacia decidió pedir") sobre las unidades
 * dispensadas.
 *
 * Devuelve `{ value, source, months }` o `null` si no hay referencia útil.
 */
function computePrevReference(line, days) {
  const pm = line?.prev_month;
  if (!pm) return null;
  if (pm.units_override != null && pm.units_override > 0) {
    return { value: pm.units_override, source: "override", months: pm.months || 1 };
  }
  const disp = Number(pm.units_dispensed || 0);
  if (disp <= 0) return null;
  const months = Math.max(1, Number(pm.months || 1));
  // Normaliza uds del estudio anterior (N meses) a la ventana actual (`days` naturales)
  const normalized = Math.round((disp * days) / (30 * months));
  return { value: normalized, source: "dispensed", months };
}

const fmtEur = (n) => Number(n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 2 });
const fmtNum = (n) => Number(n || 0).toLocaleString("es-ES");
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
};

function GhBreakdownPanel({ gh, studyLabel, loading, data, onPreferenceChange }) {
  const [saving, setSaving] = useState(false);
  const [pendingLab, setPendingLab] = useState("");

  useEffect(() => {
    setPendingLab(data?.preferred_lab || "");
  }, [data?.preferred_lab, gh]);

  const savePreference = async (newLab) => {
    setSaving(true);
    try {
      if (!newLab) {
        await api.delete(`/molecule-preferences`, { params: { gh } });
        toast.success("Preferencia eliminada");
      } else {
        await api.post(`/molecule-preferences`, { gh, preferred_lab: newLab });
        toast.success(`Lab preferente: ${newLab}`);
      }
      setPendingLab(newLab);
      if (onPreferenceChange) await onPreferenceChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando preferencia");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="px-6 py-6 text-sm text-slate-500 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin" /> Cargando detalle de {gh}…
      </div>
    );
  }
  if (!data) {
    return <div className="px-6 py-6 text-sm text-slate-500">Sin datos para este G.H.</div>;
  }
  const { marca, generico, summary, available_labs } = data;
  const Section = ({ title, tone, total, labs, emptyText }) => (
    <div>
      <div className={`flex items-baseline justify-between mb-2 ${tone === "rose" ? "text-rose-700" : "text-emerald-700"}`}>
        <div className="text-[11px] uppercase tracking-[0.18em] font-bold">{title}</div>
        <div className="text-xs font-mono-num">
          {fmtNum(total.units_total)} uds · {fmtEur(total.euros_total)}
        </div>
      </div>
      {labs.length === 0 ? (
        <div className="text-xs text-slate-400 italic">{emptyText}</div>
      ) : (
        <div className="space-y-2">
          {labs.map((lab) => (
            <div key={lab.laboratorio} className={`rounded-md border ${tone === "rose" ? "border-rose-100 bg-rose-50/40" : "border-emerald-100 bg-emerald-50/40"} p-2`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="text-xs font-bold text-slate-900 truncate">{lab.laboratorio}</div>
                <div className="text-[11px] font-mono-num text-slate-600 whitespace-nowrap">
                  {fmtNum(lab.units)} uds · {fmtEur(lab.euros)}
                </div>
              </div>
              <ul className="text-[11px] text-slate-600 font-mono-num space-y-0.5">
                {lab.products.map((p) => (
                  <li key={p.cn} className="flex items-center justify-between gap-2">
                    <span className="truncate"><span className="text-slate-400">{p.cn}</span> · {p.name}</span>
                    <span className="whitespace-nowrap">{fmtNum(p.units)} × {fmtEur(p.pvl)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
  return (
    <div className="px-6 py-5 border-l-4 border-sky-400" data-testid={`gh-breakdown-${gh}`}>
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-sky-700 flex items-center gap-2 flex-wrap">
            Detalle G.H.
            <span className="normal-case tracking-normal text-[10px] font-bold bg-sky-600 text-white rounded-full px-2 py-0.5" data-testid={`gh-breakdown-period-${gh}`}>
              Mes actual{studyLabel ? ` · ${studyLabel}` : ""}
            </span>
          </div>
          <div className="font-display text-sm font-bold text-slate-900">{gh}</div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="px-2 py-1 rounded-md bg-emerald-100 text-emerald-800 font-bold font-mono-num">
            {summary.generico_pct}% genérico
          </span>
          <span className="px-2 py-1 rounded-md bg-rose-100 text-rose-800 font-bold font-mono-num">
            {summary.marca_pct}% marca
          </span>
          <span className="text-slate-500 font-mono-num">
            · {fmtNum(summary.total_units)} uds totales
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Section title="Marca dispensada este mes" tone="rose" total={marca} labs={marca.labs} emptyText="Sin dispensación de marca en este G.H." />
        <Section title="Genérico dispensado este mes" tone="emerald" total={generico} labs={generico.labs} emptyText="Sin dispensación de genérico en este G.H." />
      </div>
      {/* Selector de laboratorio preferente */}
      <div className="mt-4 flex flex-wrap items-center gap-2 pt-3 border-t border-sky-200">
        <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-sky-700">Lab preferente:</span>
        <select
          className="text-xs border border-slate-300 rounded-md px-2 py-1.5 bg-white min-w-[200px]"
          value={pendingLab}
          disabled={saving || !available_labs?.length}
          onChange={(e) => savePreference(e.target.value)}
          data-testid={`gh-pref-select-${gh}`}
        >
          <option value="">— sin asignar —</option>
          {(available_labs || []).map((lab) => (
            <option key={lab} value={lab}>{lab}</option>
          ))}
        </select>
        {saving && <Loader2 className="h-3 w-3 animate-spin text-sky-600" />}
        {!available_labs?.length && (
          <span className="text-[11px] text-rose-700 italic">Este G.H. no tiene presentaciones genéricas en el catálogo.</span>
        )}
        <span className="text-[10px] text-slate-500 ml-auto">Cambiar el lab reasigna el pedido de este G.H.</span>
      </div>
    </div>
  );
}

export default function OrdersPage({ mode = "missing" }) {
  const isMissing = mode === "missing";
  const title = isMissing ? "Lo que nos falta" : "Pedido de reposición";
  const subtitle = isMissing
    ? "Pedido sólo para moléculas Solo Marca + Marginal con proveedor preferente asignado. Pasamos a genérico todo lo que aún compras como marca."
    : "Pedido completo: incluye reposición de genéricos ya dispensados + las moléculas de conversión marca→genérico (en rojo).";

  const [days, setDays] = useState(30);
  const [orderType, setOrderType] = useState("transfer"); // 'transfer' | 'picking'
  const [aggressive, setAggressive] = useState(false);  // false=conservador (descuenta solo labs con condición), true=agresivo (descuenta cualquier lab EFG)
  // % de las unidades de MARCA que se convertirán a genérico y hay que pedir.
  // El default es 30% — asume que ~1 de cada 3 recetas se cambia a EFG.
  // NUNCA se debe pedir el 100 % de marca + 100 % de genérico (sería duplicar).
  const [marcaConvPct, setMarcaConvPct] = useState(30);
  const [avgMonths, setAvgMonths] = useState(3); // 1 | 3 | 6 meses de media del histórico
  const [families, setFamilies] = useState([]); // grupos terapéuticos elegidos (campañas)
  const [showTherapeutic, setShowTherapeutic] = useState(false);
  const [expandLab, setExpandLab] = useState("");
  const [labList, setLabList] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(null);
  const [openLab, setOpenLab] = useState(null);
  const [showExclusions, setShowExclusions] = useState(false);
  const [exclusions, setExclusions] = useState([]);
  const [busyCn, setBusyCn] = useState(null);
  const [fourth, setFourth] = useState(null);
  const [openCandidate, setOpenCandidate] = useState(null);
  const [openCn, setOpenCn] = useState(null); // CN cuyo breakdown está abierto
  const lineSort = useSortState(); // orden compartido por todas las tablas de proveedor
  const [breakdownCache, setBreakdownCache] = useState({}); // gh → data
  const [breakdownLoading, setBreakdownLoading] = useState(null); // gh actualmente cargando

  const toggleBreakdown = async (cn, gh) => {
    if (openCn === cn) {
      setOpenCn(null);
      return;
    }
    setOpenCn(cn);
    if (!gh || breakdownCache[gh]) return;
    const dispId = data?.dispensation?.id;
    if (!dispId) return;
    setBreakdownLoading(gh);
    try {
      const { data: bd } = await api.get(`/dispensations/${dispId}/gh-breakdown`, { params: { gh } });
      setBreakdownCache((c) => ({ ...c, [gh]: bd }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el detalle");
    } finally {
      setBreakdownLoading(null);
    }
  };

  const loadExclusions = async () => {
    try {
      const r = await api.get("/orders/exclusions");
      setExclusions(r.data?.items || []);
    } catch (e) {
      // ignore
    }
  };

  const [searchParams] = useSearchParams();
  const labFromUrl = searchParams.get("lab");

  const load = async () => {
    setLoading(true);
    try {
      const [r] = await Promise.all([
        api.get(`/orders/${mode}`, { params: { days, order_type: orderType, aggressive, marca_conversion_pct: marcaConvPct, avg_months: avgMonths, families: families.join(",") || undefined, expand_lab: expandLab || undefined } }),
        loadExclusions(),
      ]);
      setData(r.data);
      // Si venimos del hub con ?lab=X, auto-expandimos esa fila
      const suppliersList = r.data?.suppliers || [];
      if (labFromUrl) {
        const found = suppliersList.find(
          (s) => (s.laboratorio || "").toLowerCase() === labFromUrl.toLowerCase(),
        );
        if (found) {
          setOpenLab(found.laboratorio);
          // Hacemos scroll a la fila después del render
          setTimeout(() => {
            const el = document.querySelector(`[data-testid="supplier-${found.laboratorio}"]`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 200);
        } else {
          setOpenLab(null);
          toast.info(`"${labFromUrl}" no aparece en este pedido`);
        }
      } else {
        setOpenLab(null);
      }
      if (isMissing) {
        try {
          const f = await api.get("/orders-fourth-suggestion", { params: { days } });
          setFourth(f.data);
        } catch (e) {
          setFourth(null);
        }
      } else {
        setFourth(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al calcular el pedido");
    } finally {
      setLoading(false);
    }
  };

  const excludeCn = async (cn, name) => {
    setBusyCn(cn);
    try {
      await api.post("/orders/exclusions", { cn, note: name || "" });
      toast.success(`CN ${cn} excluido de los pedidos`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al excluir");
    } finally {
      setBusyCn(null);
    }
  };

  const substituteCn = async (cn, name, otherLab) => {
    setBusyCn(cn);
    try {
      await api.post("/orders/exclusions", {
        cn,
        note: `Sustituido por stock de ${otherLab}${name ? " · " + name : ""}`,
      });
      toast.success(`CN ${cn} sustituido — usando stock de ${otherLab}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al sustituir");
    } finally {
      setBusyCn(null);
    }
  };

  const includeCn = async (cn) => {
    setBusyCn(cn);
    try {
      await api.delete(`/orders/exclusions/${cn}`);
      toast.success(`CN ${cn} vuelve al pedido`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al incluir");
    } finally {
      setBusyCn(null);
    }
  };

  // Iter-57: ajuste manual de la cantidad propuesta por CN.
  // No recargamos el pedido completo — mutamos in-place la línea concreta
  // en el estado local para no perder scroll / paneles abiertos.
  const [editingCn, setEditingCn] = useState(null); // CN en modo edición
  const [editValue, setEditValue] = useState("");   // string en input

  const saveOverride = async (cn, newUnitsRaw, originalUnits) => {
    const dispId = data?.dispensation?.id;
    if (!dispId) {
      toast.error("Sin estudio activo");
      return;
    }
    const parsed = parseInt(newUnitsRaw, 10);
    if (isNaN(parsed) || parsed < 0) {
      toast.error("La cantidad debe ser un número entero ≥ 0");
      return;
    }
    setBusyCn(cn);
    try {
      // 0 y "igual a la propuesta" → reset (elimina override)
      if (parsed === 0 || parsed === originalUnits) {
        await api.post("/orders/overrides", { disp_id: dispId, cn, units: 0 });
        toast.success(parsed === 0 ? `Ajuste retirado · vuelve a la propuesta` : "Sin cambios");
      } else {
        await api.post("/orders/overrides", { disp_id: dispId, cn, units: parsed });
        toast.success(`Cantidad ajustada a ${parsed} uds`);
      }
      setEditingCn(null);
      setEditValue("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar el ajuste");
    } finally {
      setBusyCn(null);
    }
  };

  const resetOverride = async (cn) => {
    const dispId = data?.dispensation?.id;
    if (!dispId) return;
    setBusyCn(cn);
    try {
      await api.delete(`/orders/overrides/${cn}`, { params: { disp_id: dispId } });
      toast.success("Ajuste retirado · vuelve a la propuesta");
      await load();
    } catch (e) {
      toast.error("No se pudo retirar el ajuste");
    } finally {
      setBusyCn(null);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, labFromUrl, orderType, aggressive, marcaConvPct, avgMonths, families, expandLab]);

  useEffect(() => {
    api.get("/supplier-agreements")
      .then(({ data }) => {
        const items = data?.items || data || [];
        setLabList([...new Set(items.map((i) => i.laboratorio).filter(Boolean))].sort());
      })
      .catch(() => {});
  }, []);

  const handleExport = async (fmt, supplier = null) => {
    setExporting(fmt);
    try {
      const supParam = supplier ? `&supplier=${encodeURIComponent(supplier)}` : "";
      const supTag = supplier ? `_${supplier.replace(/[^A-Za-z0-9]+/g, "_").slice(0, 20)}` : "";
      const otParam = `&order_type=${orderType}`;
      const aggParam = aggressive ? `&aggressive=true` : "";
      const convParam = `&marca_conversion_pct=${marcaConvPct}&avg_months=${avgMonths}`;
      const famParam = families.length ? `&families=${encodeURIComponent(families.join(","))}` : "";
      const expParam = expandLab ? `&expand_lab=${encodeURIComponent(expandLab)}` : "";
      await downloadXlsx(
        `/orders/${mode}/export?format=${fmt}&days=${days}${otParam}${aggParam}${convParam}${famParam}${expParam}${supParam}`,
        `pedido_${mode}_${days}d_${orderType}${aggressive ? "_agresivo" : ""}${supTag}.${fmt === "pdf" ? "pdf" : "xlsx"}`,
      );
    } catch (e) {
      toast.error("Error al exportar");
    } finally {
      setExporting(null);
    }
  };

  const { user } = useAuth();
  const BRAND_NAMES = { normon: "Normon", aurovitas: "Aurovitas" };
  const brandIsNormon = !!BRAND_NAMES[user?.brand];
  const brandLabel = BRAND_NAMES[user?.brand] || "Sandoz";
  const UP = brandLabel.toUpperCase();
  const brandButtons = brandIsNormon
    ? [
        { kind: "missing", label: `¿PASAR A ${UP} TODO LO QUE NO TIENES?`, help: `Moléculas donde solo dispensas marca y ${brandLabel} tiene genérico` },
        { kind: "others", label: `¿PASAR A ${UP} TODO LO QUE TIENEN OTROS?`, help: `Lo que hoy compras en genérico a otros laboratorios y ${brandLabel} fabrica` },
        { kind: "better", label: `¿PASAR A ${UP} TODO LO QUE ES MÁS RENTABLE?`, help: `Solo las moléculas donde ${brandLabel} sale más barata en neto que tu laboratorio actual` },
        { kind: "no-agreement", label: `¿PASAR A ${UP} TODO DE LABORATORIOS SIN CONDICIÓN?`, help: `Solo lo que compras a laboratorios sin condiciones cargadas y ${brandLabel} fabrica` },
      ]
    : [
        { kind: "missing", label: "¿PASAR A SANDOZ TODO LO QUE ME FALTA?", help: "Moléculas donde solo dispensas marca y Sandoz tiene genérico" },
        { kind: "no-agreement", label: "¿PASAR A SANDOZ TODO DE LABORATORIOS SIN CONDICIÓN?", help: "Lo que compras a laboratorios sin condiciones pactadas y Sandoz fabrica" },
        { kind: "rest", label: "¿Y LO QUE SANDOZ NO TIENE?", help: "Lo que Sandoz no fabrica: se asigna a tu 2º y 3º laboratorio preferente y, si ninguno lo tiene, a los laboratorios marcados con condición" },
      ];

  // Pedido directo al laboratorio de la marca: no requiere selección previa de proveedor
  const downloadSandoz = async (kind, fmt) => {
    setExporting(`sandoz-${kind}-${fmt}`);
    try {
      await downloadXlsx(
        `/orders/brand/${kind}/export?format=${fmt}`,
        `pedido_${brandLabel.toLowerCase()}_${kind}.${fmt === "pdf" ? "pdf" : "xlsx"}`,
      );
    } catch (e) {
      toast.error(`Error al generar el pedido a ${brandLabel}`);
    } finally {
      setExporting(null);
    }
  };

  const totals = data?.totals || {};
  const suppliers = data?.suppliers || [];
  const stockNote = useMemo(() => {
    if (!data?.stock?.has_stock) return "Sin stock cargado · pedido sin descontar inventario";
    return `Stock a ${fmtDate(data.stock.uploaded_at)} · ${fmtNum(data.stock.lines)} CN`;
  }, [data]);

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1500px]" data-testid={`orders-page-${mode}`}>
      <header className="mb-6 sm:mb-8 flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3 inline-flex items-center gap-2">
            {isMissing ? <PackageSearch className="h-3.5 w-3.5" /> : <Truck className="h-3.5 w-3.5" />}
            Pedido · {data?.dispensation?.label || "—"}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tighter text-slate-950">{title}</h1>
          <p className="text-slate-500 mt-2 max-w-3xl text-xs sm:text-sm">{subtitle}</p>
          {data?.demand && (
            <div
              className="mt-2 inline-flex items-center gap-1.5 bg-indigo-50 border border-indigo-200 text-indigo-800 rounded-md px-2 py-1 text-[11px]"
              data-testid="orders-demand-basis"
            >
              <History className="h-3 w-3" />
              <StudiesTooltip demand={data.demand} testid="orders-demand-hist">
                Histórico usado: <strong className="font-mono-num">{data.demand.months_covered}</strong> meses ·{" "}
                {data.demand.studies_used} {data.demand.studies_used === 1 ? "estudio" : "estudios"}
              </StudiesTooltip>
              {data.demand.short_of_target && " · menos histórico del pedido"}
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setShowExclusions(true)}
            data-testid="show-exclusions"
            className={`inline-flex items-center gap-1.5 border px-3 py-2 rounded-md text-xs font-medium ${exclusions.length > 0 ? "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"}`}
          >
            <Ban className="h-3 w-3" />
            Excluidos
            {exclusions.length > 0 && (
              <span className="font-mono-num bg-amber-200 text-amber-900 px-1.5 rounded-full text-[10px]">{exclusions.length}</span>
            )}
          </button>
          <button
            onClick={() => handleExport("xlsx")}
            disabled={!suppliers.length || exporting}
            data-testid="export-order-xlsx"
            className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-900 px-3 py-2 rounded-md text-xs font-medium hover:bg-slate-50 disabled:opacity-50"
          >
            {exporting === "xlsx" ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileSpreadsheet className="h-3 w-3" />}
            Excel
          </button>
          <button
            onClick={() => handleExport("pdf")}
            disabled={!suppliers.length || exporting}
            data-testid="export-order-pdf"
            className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-900 px-3 py-2 rounded-md text-xs font-medium hover:bg-slate-50 disabled:opacity-50"
          >
            {exporting === "pdf" ? <Loader2 className="h-3 w-3 animate-spin" /> : <FileTextIcon className="h-3 w-3" />}
            PDF
          </button>
        </div>
      </header>

      {/* Controls */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6 flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[200px]">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-1">Cobertura del pedido</div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={365}
              value={days}
              onChange={(e) => setDays(Math.max(1, Math.min(365, parseInt(e.target.value) || 1)))}
              className="w-24 px-3 py-2 border border-slate-300 rounded-md text-sm font-mono-num"
              data-testid="days-input"
            />
            <span className="text-sm text-slate-700">días</span>
            <div className="flex gap-1 ml-2">
              {[15, 30, 45, 60, 90].map((d) => (
                <button
                  key={d}
                  onClick={() => setDays(d)}
                  data-testid={`days-preset-${d}`}
                  className={`text-xs px-2.5 py-1 rounded-full border ${days === d ? "bg-slate-950 text-white border-slate-950" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                >
                  {d}d
                </button>
              ))}
            </div>
            <button
              onClick={load}
              disabled={loading}
              data-testid="recalc-order"
              className="ml-3 inline-flex items-center gap-1 bg-slate-950 text-white text-xs px-3 py-2 rounded-md hover:bg-slate-800 disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
              Recalcular
            </button>
          </div>
          <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-1.5">Tipo de pedido</div>
              <div className="flex items-center gap-2" role="radiogroup" aria-label="Tipo de pedido">
                <button
                  type="button"
                  onClick={() => setOrderType("transfer")}
                  data-testid="order-type-transfer"
                  className={`text-xs font-bold px-3 py-2 rounded-md border transition-colors ${orderType === "transfer" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                  title="Pide las unidades teóricas calculadas según consumo y cobertura"
                >
                  TRANSFER · cantidades completas
                </button>
                <button
                  type="button"
                  onClick={() => setOrderType("picking")}
                  data-testid="order-type-picking"
                  className={`text-xs font-bold px-3 py-2 rounded-md border transition-colors ${orderType === "picking" ? "bg-sky-600 text-white border-sky-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                  title="Pide sólo 1 o 2 unidades por línea (1 si las teóricas son <5, 2 si son ≥5)"
                >
                  PICKING · 1-2 uds por línea
                </button>
                <span className="text-[11px] text-slate-500 ml-2">
                  {orderType === "picking"
                    ? "Cada línea se acotará a 1 ud (si teórico <5) o 2 uds (si teórico ≥5)."
                    : "Cada línea pedirá las unidades teóricas para cubrir el consumo del periodo."}
                </span>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-1.5">Media del histórico para el consumo</div>
              <div className="flex items-center gap-2 flex-wrap" role="radiogroup" aria-label="Meses de media">
                {[1, 3, 6].map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setAvgMonths(m)}
                    data-testid={`order-avg-months-${m}`}
                    className={`text-xs font-bold px-3 py-2 rounded-md border transition-colors ${avgMonths === m ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                    title={m === 1
                      ? "Solo el estudio en uso: reacciona rápido a cambios, pero una subida corta o un mes raro distorsiona"
                      : `Media de los últimos ${m} meses de histórico: más estable, ideal si tu demanda es irregular`}
                  >
                    {m === 1 ? "1 MES · último estudio" : `${m} MESES de media`}
                  </button>
                ))}
                <span className="text-[11px] text-slate-500 ml-2 max-w-md">
                  {avgMonths === 1
                    ? "Reacciona rápido a cambios, pero si subes una semana o un mes atípico el pedido se desvía."
                    : `Suma el consumo de los últimos ${avgMonths} meses y lo divide por los meses reales. Más estable.`}
                </span>
              </div>
              {data?.demand?.short_of_target && (
                <div className="mt-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 inline-block" data-testid="order-avg-short">
                  Sólo tengo {data.demand.months_covered} meses de histórico ({data.demand.studies_used}{" "}
                  {data.demand.studies_used === 1 ? "estudio" : "estudios"}): la media se hace con lo que hay.
                </div>
              )}
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100">
              {!!user?.brand && (
                <>
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-1.5">Pedido directo a {brandLabel}</div>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {brandButtons.map((b) => (
                  <span key={b.kind} className="inline-flex items-center gap-1" title={b.help}>
                    <button
                      type="button"
                      onClick={() => downloadSandoz(b.kind, "xlsx")}
                      className={`text-[11px] font-bold px-3 py-2 rounded-md text-white transition-colors ${b.kind === "rest" ? "bg-slate-900 hover:bg-slate-700" : user?.brand === "aurovitas" ? "bg-[#3B3FA3] hover:bg-[#2c2f7d]" : brandIsNormon ? "bg-[#001E62] hover:bg-[#00143f]" : "bg-[#1F58B0] hover:bg-[#17458c]"}`}
                      data-testid={`sandoz-${b.kind}-xlsx`}
                    >
                      {b.label} · Excel
                    </button>
                    <button
                      type="button"
                      onClick={() => downloadSandoz(b.kind, "pdf")}
                      className={`text-[11px] font-bold px-2.5 py-2 rounded-md border transition-colors hover:bg-blue-50 ${user?.brand === "aurovitas" ? "border-[#3B3FA3] text-[#3B3FA3]" : brandIsNormon ? "border-[#001E62] text-[#001E62]" : "border-[#1F58B0] text-[#1F58B0]"}`}
                      data-testid={`sandoz-${b.kind}-pdf`}
                    >
                      PDF
                    </button>
                  </span>
                ))}
              </div>
                </>
              )}
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-1.5">Campaña</div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => setShowTherapeutic(true)}
                  data-testid="order-therapeutic-button"
                  className={`text-xs font-bold px-3 py-2 rounded-md border transition-colors ${
                    families.length
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-white text-indigo-700 border-indigo-300 hover:bg-indigo-50"
                  }`}
                  title="Pide solo los grupos terapéuticos de una campaña (antibióticos, antihistamínicos…)"
                >
                  ¿Quieres pedir por grupo terapéutico?
                </button>
                {data?.offer_summary && (
                  <span
                    className="inline-flex flex-wrap items-center gap-2 text-[11px] text-emerald-900 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-1.5"
                    data-testid="order-offer-saving"
                  >
                    Ahorro de la campaña:{" "}
                    <strong className="font-mono-num text-sm">
                      {data.offer_summary.saving_eur.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                    </strong>
                    <span className="text-emerald-800/80">
                      ({data.offer_summary.lines} moléculas · {data.offer_summary.cost_now.toFixed(2)} € hoy →{" "}
                      {data.offer_summary.cost_new.toFixed(2)} € con {data.offer_summary.lab})
                    </span>
                  </span>
                )}
                {families.length > 0 && (
                  <span className="inline-flex items-center gap-2 text-[11px] text-indigo-800 bg-indigo-50 border border-indigo-200 rounded-md px-2 py-1.5" data-testid="order-therapeutic-active">
                    Pidiendo solo: <strong>{families.join(", ")}</strong>
                    {expandLab && <span> · gama completa de <strong>{expandLab}</strong></span>}
                    <button
                      onClick={() => { setFamilies([]); setExpandLab(""); }}
                      className="font-bold hover:text-indigo-950"
                      data-testid="order-therapeutic-clear"
                    >
                      quitar
                    </button>
                  </span>
                )}
                {!families.length && (
                  <span className="text-[11px] text-slate-500 max-w-md">
                    Útil cuando solo quieres reponer una campaña concreta sin tocar el resto del pedido.
                  </span>
                )}
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-1.5">Política de descuento de stock</div>
              <div className="flex items-center gap-2 flex-wrap" role="radiogroup" aria-label="Política de stock">
                <button
                  type="button"
                  onClick={() => setAggressive(false)}
                  data-testid="order-policy-conservative"
                  className={`text-xs font-bold px-3 py-2 rounded-md border transition-colors ${!aggressive ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                  title="Solo descuenta unidades de stock de laboratorios CON CONDICIÓN de compra"
                >
                  CONSERVADOR · solo labs con condición
                </button>
                <button
                  type="button"
                  onClick={() => setAggressive(true)}
                  data-testid="order-policy-aggressive"
                  className={`text-xs font-bold px-3 py-2 rounded-md border transition-colors ${aggressive ? "bg-rose-600 text-white border-rose-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                  title="Descuenta el stock de CUALQUIER laboratorio EFG del mismo G.H. — útil para agotar todo el inventario antes de pedir más"
                >
                  AGRESIVO · agotar todo el stock antes de pedir
                </button>
                <span className="text-[11px] text-slate-500 ml-2 max-w-md">
                  {aggressive
                    ? "Si tienes stock EFG del mismo G.H. en cualquier lab (con o sin condición), se descuenta. Cero unidades sobrantes en estantería."
                    : "Solo se descuenta el stock de labs con los que tienes acuerdo activo (recomendado para no ahogar el rappel)."}
                </span>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold mb-1.5">
                % Conversión de marca a genérico
              </div>
              <div className="flex items-center gap-2 flex-wrap" role="radiogroup" aria-label="% conversión marca">
                {[0, 15, 30, 50, 75, 100].map((pct) => (
                  <button
                    key={pct}
                    type="button"
                    onClick={() => setMarcaConvPct(pct)}
                    data-testid={`marca-conv-${pct}`}
                    className={`text-xs font-bold px-3 py-2 rounded-md border transition-colors ${marcaConvPct === pct ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                    title={`Se pedirá el ${pct}% de las unidades vendidas en marca (asumiendo conversión a genérico)`}
                  >
                    {pct}%
                  </button>
                ))}
                <div className="inline-flex items-center gap-1 ml-2">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="5"
                    value={marcaConvPct}
                    onChange={(e) => setMarcaConvPct(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                    className="w-14 text-xs border border-slate-300 rounded px-2 py-1"
                    data-testid="marca-conv-custom"
                  />
                  <span className="text-xs text-slate-500">% personalizado</span>
                </div>
                <span className="text-[11px] text-slate-500 ml-2 max-w-md">
                  Pedido = generico + (marca × {marcaConvPct}%). Nunca se pide marca+genérico al 100% (duplicaría el pedido).
                </span>
              </div>
            </div>
        </div>
        <div className="text-xs text-slate-500 inline-flex items-center gap-2">
          <Boxes className="h-3.5 w-3.5" /> {stockNote}
        </div>
      </div>

      {/* Totals */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6" data-testid="order-totals">
          <Kpi label="Proveedores" value={fmtNum(totals.supplier_count)} />
          <Kpi label="Unidades totales" value={fmtNum(totals.total_units)} />
          <Kpi label="Valoración" value={fmtEur(totals.total_eur)} accent="emerald" />
          <Kpi label="Uds redirigidas" value={fmtNum(totals.redirected_units || 0)} accent="amber" sub={`gen→gen · ${fmtEur(totals.redirected_eur || 0)}`} />
          <Kpi label="Conversión marca→genérico" value={fmtNum(totals.conversion_units || 0)} accent="rose" sub={fmtEur(totals.conversion_eur || 0)} />
        </div>
      )}

      {/* Sugerencia 4º proveedor eliminada por petición del usuario (2026-02-22).
          El estado `fourth` y `openCandidate` sigue estando disponible por si se
          reintroduce en el futuro, pero no se renderiza. */}

      {loading ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin mx-auto mb-3" />
          Calculando pedido…
        </div>
      ) : suppliers.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-500" data-testid="no-orders">
          <Info className="h-6 w-6 mx-auto mb-3 text-slate-400" />
          {isMissing
            ? "No hay líneas que pedir. Asegúrate de haber asignado proveedor preferente a las moléculas Solo Marca / Marginal (Detalle del estudio → Proveedores → Redistribuir entre 3 labs)."
            : "No hay líneas. Sube una dispensación primero."}
        </div>
      ) : (
        <div className="space-y-3" data-testid="suppliers-list">
          {suppliers.map((s) => {
            const isOpen = openLab === s.laboratorio;
            return (
              <div key={s.laboratorio} className={`bg-white border rounded-xl overflow-hidden ${s.missing_units > 0 ? "border-rose-200" : "border-slate-200"}`}>
                <button
                  onClick={() => setOpenLab(isOpen ? null : s.laboratorio)}
                  className="w-full px-5 py-4 hover:bg-slate-50 transition-colors text-left"
                  data-testid={`supplier-${s.laboratorio}`}
                >
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      {isOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                      <div className="min-w-0">
                        <div className="font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                          {s.laboratorio}
                          {s.missing_units > 0 && (
                            <span className="text-[10px] uppercase tracking-wider font-bold bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full">
                              {fmtNum(s.missing_units)} uds en FALTA
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 font-mono-num">
                          {s.lines.length} líneas · {fmtNum(s.total_units)} uds
                          {s.own_units > 0 && (
                            <> · <span className="text-emerald-600">{fmtNum(s.own_units)} propias</span></>
                          )}
                          {s.redirected_units > 0 && (
                            <> · <span className="text-amber-600">{fmtNum(s.redirected_units)} redirigidas</span></>
                          )}
                          {s.conversion_units > 0 && (
                            <> · <span className="text-rose-600">{fmtNum(s.conversion_units)} conversión</span></>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleExport("xlsx", s.laboratorio); }}
                          disabled={exporting}
                          title={`Excel del pedido a ${s.laboratorio}`}
                          data-testid={`export-supplier-xlsx-${s.laboratorio}`}
                          className="inline-flex items-center gap-1 bg-white border border-slate-200 text-slate-700 px-2.5 py-1.5 rounded-md text-[10px] font-semibold hover:bg-slate-50 disabled:opacity-50"
                        >
                          <FileSpreadsheet className="h-3 w-3" /> Excel
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleExport("pdf", s.laboratorio); }}
                          disabled={exporting}
                          title={`PDF del pedido a ${s.laboratorio}`}
                          data-testid={`export-supplier-pdf-${s.laboratorio}`}
                          className="inline-flex items-center gap-1 bg-white border border-slate-200 text-slate-700 px-2.5 py-1.5 rounded-md text-[10px] font-semibold hover:bg-slate-50 disabled:opacity-50"
                        >
                          <FileTextIcon className="h-3 w-3" /> PDF
                        </button>
                      </div>
                      <div className="font-display text-2xl font-bold tracking-tighter font-mono-num">{fmtEur(s.total_eur)}</div>
                    </div>
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-slate-200 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                        <tr>
                          <SortTh field="cn" sort={lineSort} className="p-3 font-semibold">CN</SortTh>
                          <SortTh field="name" sort={lineSort} className="p-3 font-semibold">Descripción · G.H.</SortTh>
                          <SortTh field="units" sort={lineSort} align="right" className="p-3 font-semibold">Uds a pedir</SortTh>
                          <SortTh field="units_redirected" sort={lineSort} align="right" className="p-3 font-semibold text-amber-700" title="Genérico de otro lab redirigido al preferente">Redirigidas</SortTh>
                          <SortTh field="units_conversion" sort={lineSort} align="right" className="p-3 font-semibold text-rose-700" title="Marca convertida a genérico hacia el preferente">Conversión</SortTh>
                          <SortTh field="pvl" sort={lineSort} align="right" className="p-3 font-semibold">PVL</SortTh>
                          <SortTh field="eur" sort={lineSort} align="right" className="p-3 font-semibold">Valoración</SortTh>
                          <SortTh field="tipo" sort={lineSort} className="p-3 font-semibold" title="Ordena por tipo de línea">Tipo</SortTh>
                          <th className="text-center p-3 font-semibold w-12"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {sortRows(s.lines, lineSort.sortField, lineSort.sortDir, {
                          tipo: (l) => (l.units_redirected > 0 && l.units_own > 0
                            ? "MIXTA"
                            : l.units_redirected > 0
                              ? "REDIRIGIDA"
                              : (Array.isArray(l.marca_conversion_from) && l.marca_conversion_from.length > 0)
                                ? "CONVERSION"
                                : "REPOSICION"),
                        }).map((l) => {
                          const isOpenDetail = openCn === l.cn;
                          return (
                            <Fragment key={l.cn}>
                              <tr
                                className={`hover:bg-slate-50/60 cursor-pointer ${l.is_offer_opportunity ? "bg-indigo-50/50" : ""} ${l.is_missing ? "bg-rose-50/40" : ""} ${isOpenDetail ? "bg-sky-50/60" : ""}`}
                                onClick={() => toggleBreakdown(l.cn, l.gh)}
                                data-testid={`order-line-${l.cn}`}
                              >
                                <td className="p-3 font-mono-num text-xs">
                                  <div className="flex items-center gap-1.5">
                                    {isOpenDetail ? <ChevronDown className="h-3 w-3 text-sky-600" /> : <ChevronRight className="h-3 w-3 text-slate-400" />}
                                    {l.cn}
                                  </div>
                                </td>
                                <td className={`p-3 ${l.is_missing ? "text-rose-700 font-medium" : ""}`}>
                                  <div className="text-sm truncate max-w-[200px] sm:max-w-[420px]" title={l.name}>{l.name || "—"}</div>
                                  <div className="text-[11px] text-slate-500 truncate max-w-[200px] sm:max-w-[420px]" title={l.gh}>{l.gh}</div>
                                  {l.is_offer_opportunity && (
                                    <div className="mt-1">
                                      <span
                                        className="inline-flex items-center gap-1 text-[10px] bg-indigo-100 text-indigo-800 px-2 py-0.5 rounded-full font-semibold"
                                        data-testid="order-offer-badge"
                                      >
                                        CAMPAÑA · hoy a {l.current_supplier} ({l.offer_pct_now}%) → {l.offer_pct_new}%
                                        {l.offer_saving_eur > 0 && (
                                          <span className="text-emerald-700 font-bold">
                                            · ahorras {l.offer_saving_eur.toFixed(2)} €
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  )}
                                  {l.has_stock_other_lab > 0 && l.other_lab && (
                                    <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                                      <span className="inline-flex items-center gap-1 text-[10px] bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-semibold">
                                        <AlertTriangle className="h-3 w-3" />
                                        Tienes {l.has_stock_other_lab} uds de {l.other_lab}
                                      </span>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); substituteCn(l.cn, l.name, l.other_lab); }}
                                        disabled={busyCn === l.cn}
                                        data-testid={`substitute-${l.cn}`}
                                        title={`Excluir este CN del pedido y usar el stock de ${l.other_lab}`}
                                        className="inline-flex items-center gap-1 text-[10px] bg-emerald-600 text-white hover:bg-emerald-700 px-2 py-0.5 rounded-full font-bold disabled:opacity-50"
                                      >
                                        {busyCn === l.cn ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowRightLeft className="h-3 w-3" />}
                                        Sustituir por {l.other_lab}
                                      </button>
                                    </div>
                                  )}
                                  {l.brand_stock_total > 0 && (
                                    <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                                      <span
                                        className="inline-flex items-center gap-1 text-[10px] bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full font-semibold"
                                        title={`Stock de marca disponible para esta molécula · ajusta tu pedido de marca al laboratorio comercial restando estas unidades`}
                                        data-testid={`brand-stock-${l.cn}`}
                                      >
                                        <Pill className="h-3 w-3" />
                                        Tienes {l.brand_stock_total} uds de marca · rebaja tu pedido de marca
                                      </span>
                                    </div>
                                  )}
                                </td>
                                <td className={`p-3 text-right font-mono-num font-semibold ${l.is_missing ? "text-rose-700" : ""}`}>
                                  {editingCn === l.cn ? (
                                    <div className="inline-flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                      <input
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={editValue}
                                        onChange={(e) => setEditValue(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === "Enter") saveOverride(l.cn, editValue, l.original_units ?? l.units);
                                          if (e.key === "Escape") { setEditingCn(null); setEditValue(""); }
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                        autoFocus
                                        data-testid={`override-input-${l.cn}`}
                                        className="w-20 px-2 py-1 text-right font-mono-num text-sm border border-sky-400 rounded focus:outline-none focus:ring-2 focus:ring-sky-300"
                                      />
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); saveOverride(l.cn, editValue, l.original_units ?? l.units); }}
                                        disabled={busyCn === l.cn}
                                        data-testid={`override-save-${l.cn}`}
                                        className="p-1 rounded text-white bg-sky-600 hover:bg-sky-700 disabled:opacity-50"
                                        title="Guardar (Enter)"
                                      >
                                        {busyCn === l.cn ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                                      </button>
                                      <button
                                        type="button"
                                        onClick={(e) => { e.stopPropagation(); setEditingCn(null); setEditValue(""); }}
                                        data-testid={`override-cancel-${l.cn}`}
                                        className="p-1 rounded text-slate-500 hover:bg-slate-100"
                                        title="Cancelar (Esc)"
                                      >
                                        <X className="h-3.5 w-3.5" />
                                      </button>
                                    </div>
                                  ) : (
                                    <div className="inline-flex flex-col items-end gap-0.5">
                                      <div className="inline-flex items-center gap-1.5">
                                        <button
                                          type="button"
                                          onClick={(e) => { e.stopPropagation(); setEditingCn(l.cn); setEditValue(String(l.units)); }}
                                          data-testid={`override-open-${l.cn}`}
                                          title={l.is_override ? `Cantidad ajustada manualmente (propuesta original: ${l.original_units} uds). Click para editar.` : "Ajustar cantidad manualmente"}
                                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded hover:bg-sky-50 hover:text-sky-700 ${l.is_override ? "bg-sky-100 text-sky-800 ring-1 ring-sky-300" : ""}`}
                                        >
                                          {fmtNum(l.units)}
                                          <Pencil className="h-3 w-3 opacity-60" />
                                        </button>
                                        {l.is_override && (
                                          <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); resetOverride(l.cn); }}
                                            disabled={busyCn === l.cn}
                                            data-testid={`override-reset-${l.cn}`}
                                            title={`Volver a la propuesta original: ${l.original_units} uds`}
                                            className="text-[10px] uppercase tracking-wider font-bold text-slate-500 hover:text-slate-900 hover:bg-slate-100 px-1.5 py-0.5 rounded disabled:opacity-50"
                                          >
                                            reset
                                          </button>
                                        )}
                                      </div>
                                      {l.prev_month && (l.prev_month.units_dispensed > 0 || l.prev_month.units_override != null) && (() => {
                                        const prevRef = computePrevReference(l, days);
                                        if (!prevRef) return null;
                                        const delta = l.units - prevRef.value;
                                        const pct = prevRef.value > 0 ? (delta / prevRef.value) * 100 : 0;
                                        // Outlier: propuesta ≥3× lo del mes previo (con referencia mínima
                                        // de 5 uds para evitar ruido en cantidades muy pequeñas).
                                        const isOutlier = prevRef.value >= 5 && l.units >= 3 * prevRef.value;
                                        const isDown  = delta < 0;
                                        const isEq    = delta === 0;
                                        const deltaColor = isEq
                                          ? "text-slate-400"
                                          : isDown
                                            ? "text-emerald-600"
                                            : "text-amber-700";
                                        const DeltaIcon = isEq ? Minus : (isDown ? TrendingDown : TrendingUp);
                                        const deltaSign = delta > 0 ? "+" : "";
                                        const srcLabel = prevRef.source === "override"
                                          ? `Mes ant. pedido: ${fmtNum(prevRef.value)} uds`
                                          : `Mes ant. disp.: ${fmtNum(prevRef.value)} uds${prevRef.months > 1 ? " (norm.)" : ""}`;
                                        const tooltipBase = `Estudio anterior (${l.prev_month.disp_label || "—"}${l.prev_month.months ? " · " + l.prev_month.months + " mes(es)" : ""}): ${fmtNum(l.prev_month.units_dispensed)} uds dispensadas${l.prev_month.units_override != null ? " · pediste " + fmtNum(l.prev_month.units_override) + " uds" : ""}. Comparado con la ventana actual de ${days} días.`;
                                        return (
                                          <div className="inline-flex items-center gap-1.5 flex-wrap justify-end mt-0.5">
                                            <span
                                              className="text-[10px] font-mono-num text-slate-500 font-normal whitespace-nowrap"
                                              data-testid={`prev-month-${l.cn}`}
                                              title={tooltipBase}
                                            >
                                              {prevRef.source === "override" ? (
                                                <>Mes ant. pedido: <strong className="text-sky-700">{fmtNum(prevRef.value)}</strong> uds</>
                                              ) : (
                                                <>Mes ant. disp.: <strong className="text-slate-700">{fmtNum(prevRef.value)}</strong> uds{prevRef.months > 1 && <span className="text-slate-400"> (norm.)</span>}</>
                                              )}
                                            </span>
                                            <span
                                              className={`inline-flex items-center gap-0.5 text-[10px] font-mono-num font-bold ${deltaColor}`}
                                              data-testid={`prev-delta-${l.cn}`}
                                              title={`Δ vs mes anterior: ${deltaSign}${fmtNum(delta)} uds (${deltaSign}${pct.toFixed(0)}%)`}
                                            >
                                              <DeltaIcon className="h-2.5 w-2.5" />
                                              {deltaSign}{fmtNum(delta)}
                                              {prevRef.value > 0 && Math.abs(pct) >= 1 && (
                                                <span className="ml-0.5">({deltaSign}{Math.round(pct)}%)</span>
                                              )}
                                            </span>
                                            {isOutlier && (
                                              <span
                                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-orange-100 text-orange-800 border border-orange-300 px-1.5 py-0.5 rounded-full whitespace-nowrap"
                                                data-testid={`outlier-warning-${l.cn}`}
                                                title={`La propuesta actual (${fmtNum(l.units)} uds) es ≥3× lo del mes anterior (${fmtNum(prevRef.value)} uds). Revisa si tiene sentido antes de pedir.`}
                                              >
                                                <AlertTriangle className="h-3 w-3" />
                                                Muy superior a lo habitual
                                              </span>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </td>
                                <td className="p-3 text-right font-mono-num text-amber-700 font-semibold">
                                  {l.units_redirected > 0 ? fmtNum(l.units_redirected) : "—"}
                                </td>
                                <td className="p-3 text-right font-mono-num text-rose-700 font-semibold">
                                  {l.units_conversion > 0 ? fmtNum(l.units_conversion) : "—"}
                                </td>
                                <td className="p-3 text-right font-mono-num text-slate-600">{fmtEur(l.pvl)}</td>
                                <td className={`p-3 text-right font-mono-num font-bold ${l.is_missing ? "text-rose-700" : "text-slate-900"}`}>{fmtEur(l.eur)}</td>
                                <td className="p-3">
                                  {l.units_redirected > 0 && l.units_own > 0 ? (
                                    <span className="text-[10px] uppercase tracking-wider font-bold bg-violet-100 text-violet-800 px-2 py-0.5 rounded-full" title="Mezcla de unidades redirigidas (gen→gen) + resto a pedir">MIXTA</span>
                                  ) : l.units_redirected > 0 ? (
                                    <span className="text-[10px] uppercase tracking-wider font-bold bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full" title="Redirigida: cambio de proveedor de genérico hacia el preferente">REDIRIGIDA</span>
                                  ) : (Array.isArray(l.marca_conversion_from) && l.marca_conversion_from.length > 0) ? (
                                    <span className="text-[10px] uppercase tracking-wider font-bold bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full" title="Conversión marca → genérico hacia el laboratorio preferente">CONVERSIÓN</span>
                                  ) : (
                                    <span className="text-[10px] uppercase tracking-wider font-bold bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">Reposición</span>
                                  )}
                                </td>
                                <td className="p-3 text-center">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); excludeCn(l.cn, l.name); }}
                                    disabled={busyCn === l.cn}
                                    title="Excluir este CN de futuros pedidos"
                                    data-testid={`exclude-${l.cn}`}
                                    className="inline-flex items-center justify-center w-7 h-7 rounded-full text-slate-400 hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
                                  >
                                    {busyCn === l.cn ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Ban className="h-3.5 w-3.5" />}
                                  </button>
                                </td>
                              </tr>
                              {isOpenDetail && (
                                <tr className="bg-sky-50/40">
                                  <td colSpan={9} className="p-0">
                                    <OrderCalcBreakdown line={l} demand={data.demand} avgMonths={avgMonths} testid={`order-calc-${l.cn}`} />
                                    {(Array.isArray(l.redirected_from) && l.redirected_from.length > 0) || (Array.isArray(l.marca_conversion_from) && l.marca_conversion_from.length > 0) || (Array.isArray(l.own_breakdown) && l.own_breakdown.length > 0) ? (
                                      <div className="px-6 py-4 border-b border-sky-200/60">
                                        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2 flex items-center gap-2 flex-wrap">
                                          Origen de las unidades pedidas
                                          <span className="normal-case tracking-normal font-bold bg-indigo-600 text-white rounded-full px-2 py-0.5" data-testid={`order-origin-period-${l.cn}`}>
                                            <StudiesTooltip demand={data?.demand} cns={lineSourceCns(l)} testid={`order-origin-hist-${l.cn}`}>
                                              Histórico usado{data?.demand ? ` · ${String(data.demand.months_covered).replace(".", ",")} meses · ${data.demand.studies_used} ${data.demand.studies_used === 1 ? "estudio" : "estudios"}` : ""}
                                            </StudiesTooltip>
                                          </span>
                                        </div>
                                        {Array.isArray(l.redirected_from) && l.redirected_from.length > 0 && (
                                          <div className="mb-3">
                                            <div className="text-xs font-bold text-amber-700 mb-1">
                                              Redirigidas (otro genérico → {s.laboratorio}) — {fmtNum(l.units_redirected || 0)} uds en este pedido
                                            </div>
                                            <table className="w-full text-xs bg-white rounded-md overflow-hidden border border-amber-200" data-testid={`redirected-from-${l.cn}`}>
                                              <thead className="bg-amber-50 text-[10px] uppercase tracking-wider text-amber-800">
                                                <tr>
                                                  <th className="text-left p-2 font-semibold">CN origen</th>
                                                  <th className="text-left p-2 font-semibold">Descripción</th>
                                                  <th className="text-left p-2 font-semibold">Laboratorio</th>
                                                  <th className="text-right p-2 font-semibold">Uds dispensadas (histórico)</th>
                                                </tr>
                                              </thead>
                                              <tbody className="divide-y divide-amber-100">
                                                {l.redirected_from.map((r, idx) => (
                                                  <tr key={`r-${r.cn}-${idx}`} className="hover:bg-amber-50/40">
                                                    <td className="p-2 font-mono-num text-[11px]">{r.cn || "—"}</td>
                                                    <td className="p-2 truncate max-w-[320px]" title={r.name}>{r.name || "—"}</td>
                                                    <td className="p-2">{r.lab || "—"}</td>
                                                    <td className="p-2 text-right font-mono-num font-semibold">{fmtNum(r.units)}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        )}
                                        {Array.isArray(l.marca_conversion_from) && l.marca_conversion_from.length > 0 && (
                                          <div className="mb-3">
                                            <div className="text-xs font-bold text-rose-700 mb-1">
                                              Conversión marca → genérico ({s.laboratorio})
                                            </div>
                                            <table className="w-full text-xs bg-white rounded-md overflow-hidden border border-rose-200" data-testid={`marca-conversion-${l.cn}`}>
                                              <thead className="bg-rose-50 text-[10px] uppercase tracking-wider text-rose-800">
                                                <tr>
                                                  <th className="text-left p-2 font-semibold">CN origen</th>
                                                  <th className="text-left p-2 font-semibold">Descripción</th>
                                                  <th className="text-left p-2 font-semibold">Laboratorio</th>
                                                  <th className="text-right p-2 font-semibold">Uds dispensadas marca (histórico)</th>
                                                </tr>
                                              </thead>
                                              <tbody className="divide-y divide-rose-100">
                                                {l.marca_conversion_from.map((r, idx) => (
                                                  <tr key={`m-${r.cn}-${idx}`} className="hover:bg-rose-50/40">
                                                    <td className="p-2 font-mono-num text-[11px]">{r.cn || "—"}</td>
                                                    <td className="p-2 truncate max-w-[320px]" title={r.name}>{r.name || "—"}</td>
                                                    <td className="p-2">{r.lab || "—"}</td>
                                                    <td className="p-2 text-right font-mono-num font-semibold">{fmtNum(r.units)}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        )}
                                        {Array.isArray(l.own_breakdown) && l.own_breakdown.length > 0 && l.units_own > 0 && (
                                          <div>
                                            <div className="text-xs font-bold text-emerald-700 mb-1">
                                              Reposición de {s.laboratorio} (genérico ya tuyo)
                                            </div>
                                            <table className="w-full text-xs bg-white rounded-md overflow-hidden border border-emerald-200" data-testid={`own-breakdown-${l.cn}`}>
                                              <thead className="bg-emerald-50 text-[10px] uppercase tracking-wider text-emerald-800">
                                                <tr>
                                                  <th className="text-left p-2 font-semibold">CN</th>
                                                  <th className="text-left p-2 font-semibold">Descripción</th>
                                                  <th className="text-right p-2 font-semibold">Uds dispensadas (histórico)</th>
                                                </tr>
                                              </thead>
                                              <tbody className="divide-y divide-emerald-100">
                                                {l.own_breakdown.map((r, idx) => (
                                                  <tr key={`o-${r.cn}-${idx}`} className="hover:bg-emerald-50/40">
                                                    <td className="p-2 font-mono-num text-[11px]">{r.cn || "—"}</td>
                                                    <td className="p-2 truncate max-w-[320px]" title={r.name}>{r.name || "—"}</td>
                                                    <td className="p-2 text-right font-mono-num font-semibold">{fmtNum(r.units)}</td>
                                                  </tr>
                                                ))}
                                              </tbody>
                                            </table>
                                          </div>
                                        )}
                                      </div>
                                    ) : null}
                                    {Array.isArray(l.brand_stock_breakdown) && l.brand_stock_breakdown.length > 0 && (
                                      <div className="px-6 py-4 border-b border-sky-200/60" data-testid={`brand-stock-detail-${l.cn}`}>
                                        <div className="text-xs font-bold text-rose-700 mb-1.5 flex items-center gap-1.5">
                                          <Pill className="h-3.5 w-3.5" />
                                          Stock de marca de esta molécula ({l.brand_stock_total} uds)
                                        </div>
                                        <div className="text-[11px] text-slate-600 mb-2">
                                          Ya tienes estas presentaciones de marca en estantería. Rebaja tu pedido de marca al laboratorio comercial restando estas unidades.
                                        </div>
                                        <div className="bg-white rounded border border-rose-200 overflow-hidden">
                                          <table className="w-full text-xs">
                                            <thead className="bg-rose-50">
                                              <tr>
                                                <th className="text-left p-2 font-bold text-slate-700">CN</th>
                                                <th className="text-left p-2 font-bold text-slate-700">Presentación</th>
                                                <th className="text-left p-2 font-bold text-slate-700">Laboratorio</th>
                                                <th className="text-right p-2 font-bold text-slate-700">Uds. en stock</th>
                                              </tr>
                                            </thead>
                                            <tbody className="divide-y divide-rose-100">
                                              {l.brand_stock_breakdown.map((b, idx) => (
                                                <tr key={`b-${b.cn}-${idx}`} className="hover:bg-rose-50/40">
                                                  <td className="p-2 font-mono-num text-[11px]">{b.cn || "—"}</td>
                                                  <td className="p-2 truncate max-w-[280px]" title={b.name}>{b.name || "—"}</td>
                                                  <td className="p-2 text-slate-600 truncate max-w-[180px]" title={b.lab}>{b.lab || "—"}</td>
                                                  <td className="p-2 text-right font-mono-num font-semibold text-rose-700">{fmtNum(b.units_stock)}</td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    )}
                                    {/* Stock genérico ya existente en la farmacia para esta molécula (todos labs) */}
                                    {data?.stock?.has_stock && (
                                      <div className="px-6 py-4 border-b border-sky-200/60" data-testid={`generic-stock-detail-${l.cn}`}>
                                        <div className="text-xs font-bold text-emerald-700 mb-1.5 flex items-center gap-1.5">
                                          <Boxes className="h-3.5 w-3.5" />
                                          Stock EFG (genérico) ya en tu farmacia ({l.generic_stock_total || 0} uds)
                                        </div>
                                        {(l.generic_stock_total || 0) === 0 && (l.cn_own_stock || 0) === 0 ? (
                                          <div className="text-[11px] text-slate-500 italic bg-white border border-slate-200 rounded p-2">
                                            No tienes ninguna presentación EFG de esta molécula en estantería — no puedes cubrir esta reposición con stock existente.
                                          </div>
                                        ) : (
                                          <>
                                            <div className="text-[11px] text-slate-600 mb-2">
                                              {(l.cn_own_stock || 0) > 0 && (
                                                <span className="inline-block bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-semibold text-[10px] mr-2 mb-1">
                                                  Este CN ya tiene {l.cn_own_stock} uds en estantería
                                                </span>
                                              )}
                                              Antes de confirmar, contrasta si con este stock ya cubres tu venta y no hace falta pedir tanto.
                                            </div>
                                            {Array.isArray(l.generic_stock_breakdown) && l.generic_stock_breakdown.length > 0 && (
                                              <div className="bg-white rounded border border-emerald-200 overflow-hidden">
                                                <table className="w-full text-xs">
                                                  <thead className="bg-emerald-50">
                                                    <tr>
                                                      <th className="text-left p-2 font-bold text-slate-700">CN</th>
                                                      <th className="text-left p-2 font-bold text-slate-700">Presentación</th>
                                                      <th className="text-left p-2 font-bold text-slate-700">Laboratorio</th>
                                                      <th className="text-right p-2 font-bold text-slate-700">Uds. en stock</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody className="divide-y divide-emerald-100">
                                                    {l.generic_stock_breakdown.map((g, idx) => (
                                                      <tr key={`gs-${g.cn}-${idx}`} className={`hover:bg-emerald-50/40 ${g.cn === l.cn ? "bg-emerald-50" : ""}`}>
                                                        <td className="p-2 font-mono-num text-[11px]">
                                                          {g.cn || "—"}
                                                          {g.cn === l.cn && <span className="ml-1 text-[9px] font-bold text-emerald-800">← este</span>}
                                                        </td>
                                                        <td className="p-2 truncate max-w-[280px]" title={g.name}>{g.name || "—"}</td>
                                                        <td className="p-2 text-slate-600 truncate max-w-[180px]" title={g.lab}>{g.lab || "—"}</td>
                                                        <td className="p-2 text-right font-mono-num font-semibold text-emerald-700">{fmtNum(g.units_stock)}</td>
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                </table>
                                              </div>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    )}
                                    <GhBreakdownPanel
                                      gh={l.gh}
                                      studyLabel={data?.dispensation?.label}
                                      loading={breakdownLoading === l.gh}
                                      data={breakdownCache[l.gh]}
                                      onPreferenceChange={async () => {
                                        // Recarga del pedido + invalidar cache GH
                                        setBreakdownCache((c) => {
                                          const cc = { ...c };
                                          delete cc[l.gh];
                                          return cc;
                                        });
                                        await load();
                                      }}
                                    />
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                        <tr className="bg-emerald-50/50 font-bold">
                          <td colSpan={2} className="p-3 text-right">TOTAL {s.laboratorio}</td>
                          <td className="p-3 text-right font-mono-num">{fmtNum(s.total_units)}</td>
                          <td className="p-3 text-right font-mono-num text-amber-700">{s.redirected_units > 0 ? fmtNum(s.redirected_units) : "—"}</td>
                          <td className="p-3 text-right font-mono-num text-rose-700">{s.conversion_units > 0 ? fmtNum(s.conversion_units) : "—"}</td>
                          <td />
                          <td className="p-3 text-right font-mono-num text-emerald-800">{fmtEur(s.total_eur)}</td>
                          <td />
                          <td />
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      {/* Modal de exclusiones */}
      {showExclusions && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-3 sm:p-8 overflow-y-auto" onClick={() => setShowExclusions(false)} data-testid="exclusions-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-4 sm:p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-bold mb-1">CN excluidos del pedido</div>
                <div className="font-display text-2xl font-bold tracking-tight inline-flex items-center gap-2">
                  <Ban className="h-5 w-5 text-amber-600" />
                  {exclusions.length} producto{exclusions.length !== 1 ? "s" : ""} fuera de los pedidos
                </div>
              </div>
              <button onClick={() => setShowExclusions(false)} className="text-slate-400 hover:text-slate-900" data-testid="close-exclusions">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-sm text-slate-600 mb-4">
              Los CN aquí listados no aparecerán en <em>Lo que nos falta</em> ni en <em>Pedido reposición</em>, aunque salgan en falta. Útil para productos que la farmacia ha decidido no convertir o no aprovisionar todavía.
            </p>
            {exclusions.length === 0 ? (
              <div className="text-center text-slate-400 italic text-sm py-8 border border-dashed border-slate-200 rounded-lg">
                Aún no has excluido ningún CN. Usa el botón <Ban className="inline h-3 w-3 mx-1" /> en cualquier línea del pedido para excluirla.
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto border border-slate-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
                    <tr>
                      <th className="text-left p-3 font-semibold">CN</th>
                      <th className="text-left p-3 font-semibold">Producto</th>
                      <th className="text-right p-3 font-semibold w-20"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {exclusions.map((x) => (
                      <tr key={x.cn} className="hover:bg-slate-50">
                        <td className="p-3 font-mono-num">{x.cn}</td>
                        <td className="p-3 text-slate-700 text-xs truncate max-w-[420px]" title={x.note}>{x.note || "—"}</td>
                        <td className="p-3 text-right">
                          <button
                            onClick={() => includeCn(x.cn)}
                            disabled={busyCn === x.cn}
                            className="text-xs px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                            data-testid={`include-${x.cn}`}
                          >
                            {busyCn === x.cn ? <Loader2 className="h-3 w-3 animate-spin inline" /> : "Volver al pedido"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {showTherapeutic && (
        <TherapeuticPicker
          groups={data?.therapeutic_groups || []}
          selected={families}
          onClose={() => setShowTherapeutic(false)}
          labs={[...new Set([...(data?.suppliers || []).map((x) => x.laboratorio), ...labList])]
            .filter(Boolean)
            .sort((a, b) => a.localeCompare(b, "es"))}
          expandLab={expandLab}
          onApply={(list, lab) => {
            setFamilies(list);
            setExpandLab(list.length ? lab || "" : "");
            setShowTherapeutic(false);
          }}
        />
      )}
    </div>
  );
}

function Kpi({ label, value, accent = "slate", sub }) {
  const c = { emerald: "text-emerald-700", rose: "text-rose-700", slate: "text-slate-900" }[accent];
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{label}</div>
      <div className={`font-display text-2xl font-extrabold mt-1 ${c}`}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}
